import {
  INDUSTRY_CLIENT_ID,
  NonProdishClientsConfig,
  NonProdishRIClientsConfig,
  ProdishAuth0Domain,
  ProdishClientsConfig,
  ProdishRIClientsConfig,
  Product,
  RETAIL_CLIENT_ID,
  Slide,
  SlidesByType,
  UpsellConfig,
  getConfig,
} from '../common';
import { UpsellCell } from '../common/client/types';
import { RetailMarkets } from '../services/Retail.service';

const EMPTY_RETURN: ReturnType = {
  slides: [],
};

type ReturnType = {
  slides: Slide[];
  slidesType?: SlidesByType;
  market?: string;
};

const isRequiredDataMissing = (
  auth0Domain: string,
  isMarketProduct: boolean,
  isRetailProduct: boolean,
  itemGroup: string,
  country: string,
  retailMarkets: RetailMarkets,
  clientId?: number
) =>
  !clientId ||
  !auth0Domain ||
  (isMarketProduct && (!itemGroup || !country)) ||
  (isRetailProduct && retailMarkets.retailInsights.length === 0 && retailMarkets.retailHitlistStandard.length === 0);

const getClientConfig = (clientsConfig, clientId: number, isMarketProduct: boolean, isRetailProduct: boolean) =>
  clientsConfig.find(config =>
    [
      clientId.toString(),
      ...(isMarketProduct ? [INDUSTRY_CLIENT_ID] : []),
      ...(isRetailProduct ? [RETAIL_CLIENT_ID] : []),
    ].includes(config.id)
  );

const generateResult = (isMarketProduct, isRetailProduct, itemGroup, country, clientConfig) => {
  let result: ReturnType = { ...EMPTY_RETURN };
  let slidesByMarket: UpsellCell = {};
  let slidesByType = {};
  let slides = [];
  let slidesType = undefined;

  if (isMarketProduct) {
    slidesByMarket = UpsellConfig[Product.Market][clientConfig.key];
  } else if (isRetailProduct) {
    slidesByMarket = UpsellConfig[Product.Retail][clientConfig.key];
  }

  if (slidesByMarket) {
    slidesByType = slidesByMarket.all || slidesByMarket[`${itemGroup}/${country}`];
    if (slidesByType?.[SlidesByType.CONSUMER_RETAIL_LOST_SHOPPER]?.length > 0) {
      slides = slidesByType[SlidesByType.CONSUMER_RETAIL_LOST_SHOPPER];
      slidesType = SlidesByType.CONSUMER_RETAIL_LOST_SHOPPER;
    } else if (slidesByType?.[SlidesByType.CONSUMER_LOST_SHOPPER]?.length > 0) {
      slides = slidesByType?.[SlidesByType.CONSUMER_LOST_SHOPPER];
      slidesType = SlidesByType.CONSUMER_LOST_SHOPPER;
    } else if (slidesByType?.[SlidesByType.DEFAULT]?.length > 0) {
      slides = slidesByType?.[SlidesByType.DEFAULT];
      slidesType = SlidesByType.DEFAULT;
    }

    result = {
      market: `${itemGroup}/${country}`,
      slides,
      slidesType,
    };
  }
  return result;
};

export const getUpsellCellSlides = (
  itemGroup: string,
  country: string,
  retailMarkets: RetailMarkets,
  products: Product[],
  clientId?: number
): ReturnType => {
  const { AUTH0_DOMAIN, MFE_UPSELL_HAS_BANNERS } = getConfig();
  const isMarketProduct = products.includes(Product.Market);
  const isRetailProduct = products.includes(Product.Retail);
  if (!MFE_UPSELL_HAS_BANNERS) {
    return EMPTY_RETURN;
  }

  if (
    isRequiredDataMissing(AUTH0_DOMAIN, isMarketProduct, isRetailProduct, itemGroup, country, retailMarkets, clientId)
  ) {
    return EMPTY_RETURN;
  }

  let clientsConfig = [];
  switch (true) {
    case isMarketProduct:
      clientsConfig = AUTH0_DOMAIN === ProdishAuth0Domain ? ProdishClientsConfig : NonProdishClientsConfig;
      break;
    case isRetailProduct:
      clientsConfig = AUTH0_DOMAIN === ProdishAuth0Domain ? ProdishRIClientsConfig : NonProdishRIClientsConfig;
      break;
    default:
      break;
  }

  if (clientsConfig.length === 0) {
    return EMPTY_RETURN;
  }

  const clientConfig = getClientConfig(clientsConfig, clientId, isMarketProduct, isRetailProduct);
  if (!clientConfig) {
    return EMPTY_RETURN;
  }

  return generateResult(isMarketProduct, isRetailProduct, itemGroup, country, clientConfig);
};
